import { useApolloClient } from '@apollo/client';
import { Button, Size } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import _ from 'lodash';
import * as React from 'react';
import { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import IProject from 'types/Project';
import { t } from 'utils/localize';
import { Waiting } from '../../../..';
import { subProjectQuery } from '../project-tree-queries';
import './ProjectLeaf.scss';

interface IProjectLeafProps {
  project: IProject;
  onChange: (project: IProject) => void;
  onProjectToggled?: (project: IProject) => void;
  value: IProject | null | undefined;
  showArchived?: boolean;
  openByDefault?: boolean;
  className?: string | object;
  isTopLevel?: boolean;
}

export const ProjectLeaf: FunctionComponent<IProjectLeafProps> = (props) => {
  const { className, openByDefault, project, showArchived, onChange, onProjectToggled, value, isTopLevel } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [didInitialLoad, setDidInitialLoad] = useState<boolean>(false);
  const [subProjects, setSubProjects] = useState<IProject[]>([]);
  const [hasSubProjects, setHasSubProjects] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const prevProps = usePrevious({ value, project });
  const client = useApolloClient();

  // Open the leaf upon first render if openByDefault is set
  useEffect(() => {
    if (openByDefault) {
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load subProjects when leaf is opened for the first time
  useEffect(() => {
    if (!didInitialLoad && isOpen) {
      loadSubProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Update portions of the tree if the value(selected project) is edited
  useLayoutEffect(() => {
    handleValueDidChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // The project changed
  useEffect(() => {
    handleProjectDidChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  // Update the ui when subprojects are added/deleted or showArchived is toggled.
  useEffect(() => {
    setHasSubProjects(hasActiveSubProjects() || (showArchived === true && hasArchivedSubProjects()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subProjects, showArchived]);

  // Previous
  function usePrevious(val: any) {
    const ref = useRef();
    useEffect(() => {
      ref.current = val;
    });
    return ref.current;
  }

  // Check if the project has at least one active sub project
  function hasActiveSubProjects() {
    const childElements = !didInitialLoad && project.children ? project.children : subProjects;

    return childElements.some((child: IProject) => {
      return child.archivedOn === null;
    });
  }

  // Check if the project has at least one archived child
  function hasArchivedSubProjects() {
    const childElements = !didInitialLoad && project.children ? project.children : subProjects;

    return childElements.some((child: IProject) => {
      return child.archivedOn !== null;
    });
  }

  function getNumArchived(projects: IProject[]) {
    return projects.filter((p) => p.archivedOn !== null).length;
  }

  // The value changed
  function handleValueDidChange() {
    if (value && value!.id === project.id) {
      if (!didInitialLoad) {
        loadSubProjects();
      } else if (
        value.children &&
        (value.children.length !== subProjects.length || getNumArchived(value.children) !== getNumArchived(subProjects))
      ) {
        loadSubProjects();
      }
    }

    // Update the selected leaf if the title or archived status was edited
    if (value && value.parentProjectId === project.id) {
      const prev = prevProps as { value: IProject } | undefined;

      if (
        prev &&
        prev.value.id === value.id &&
        (prev.value.archivedOn !== value.archivedOn || prev.value.title !== value.title)
      ) {
        const newChildren = subProjects.map((child: IProject) => {
          return child.id === value.id ? { ...child, title: value.title, archivedOn: value.archivedOn } : child;
        });

        setSubProjects(newChildren);
      }
    }
  }

  // If the project's archived status has been changed, update the subProjects as well
  // This recursively updates the archived status of all descendent project leaf components.
  function handleProjectDidChange() {
    if (project) {
      const prev = prevProps! as { project: IProject };

      if (prev && prev.project.archivedOn !== project.archivedOn) {
        const newChildren = subProjects.map((child: IProject) => {
          return { ...child, archivedOn: project.archivedOn };
        });

        setSubProjects(newChildren);
      }
    }
  }

  // Fetch all subProjects(sub projects) for the leaf
  async function getSubProjects(projectId: string, forceNetwork: boolean = false): Promise<IProject[]> {
    return new Promise((resolve, reject) => {
      client
        .query({
          fetchPolicy: forceNetwork ? 'network-only' : 'cache-first',
          query: subProjectQuery,
          variables: { projectId },
        })
        .then((result: any) => {
          resolve(result.data.projects[0].children);
        })
        .catch(() => {
          reject();
        });
    });
  }

  // Fetch the subProjects and load them into the state
  async function loadSubProjects() {
    setLoading(true);

    if (!didInitialLoad) {
      setDidInitialLoad(true);
    }

    const results = await getSubProjects(project.id, true);
    setLoading(false);
    setSubProjects(_.sortBy(results, (subProject) => subProject.title!.toLowerCase()));
  }

  // Click handler for expanding button
  function handleButtonClick(e: React.MouseEvent) {
    e.stopPropagation();
    setIsOpen(!isOpen);

    if (onProjectToggled) {
      onProjectToggled(project);
    }
  }

  // Event handler for link used to set the new value
  function handleLinkClick(e: React.MouseEvent) {
    e.stopPropagation();
    onChange(project);
  }

  // Render a project leaf
  function renderChild(child: IProject) {
    if (child.archivedOn === null || showArchived) {
      return (
        <ProjectLeaf
          project={child}
          value={value}
          openByDefault={(value?.depth ?? 0) >= child.depth}
          showArchived={showArchived}
          onChange={onChange}
          onProjectToggled={onProjectToggled}
        />
      );
    }
  }

  // Render the expanding button
  function renderButton() {
    const buttonLabel = loading ? <Waiting size="small" /> : isOpen ? <>-</> : <>+</>;

    return (
      <Button className="project-leaf-btn" onClick={handleButtonClick} type="secondary" size={Size.SMALL}>
        {buttonLabel}
      </Button>
    );
  }

  const classes = classNames(
    {
      'archived': !_.isNil(project.archivedOn),
      'project-leaf': true,
      'selected': value && value.id === project.id,
    },
    className
  );

  return (
    <>
      {isTopLevel && !hasSubProjects && (
        <div className="project-tree-empty fc-3">
          <span className="">{t('No Subprojects')}</span>
        </div>
      )}

      {(isTopLevel !== true || (isTopLevel === true && hasSubProjects)) && (
        <div className={classes}>
          <div className="leaf-line" />
          <div className="leaf-content">
            <div className="project-leaf-row">
              <div className="column-line" />
              <div className="row-content">
                {isTopLevel && !hasSubProjects && <div>Empty</div>}
                {hasSubProjects && renderButton()}
                {(!isTopLevel || (isTopLevel && hasSubProjects)) && (
                  <div className="row-title" onClick={handleLinkClick}>
                    {project.title}
                  </div>
                )}
                <span className="row-line" />
              </div>
            </div>
            {isOpen && hasSubProjects && (
              <div className="project-leaf-children">
                {subProjects!.map((child: IProject) => (
                  <React.Fragment key={child.id}>{renderChild(child)}</React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectLeaf;
